/**
 * <salon-request>: de aanvraagsectie van de salonpagina (sections/salon-request.liquid).
 * Het formulier is een Shopify Forms-blok; dat publiceert zelf niets naar de pagina.
 *
 * Dit publiceert b2b_lead_submit {salon_type, uses[], trial} via
 * Shopify.analytics.publish, en pas als het formulier echt verstuurd is: bij het
 * verzenden wordt de keuze onthouden, en zodra Forms het formulier vervangt door zijn
 * succesmelding gaat het event weg. Een formulier met fouten blijft staan, dus dan
 * gebeurt er niets. Geen naam, e-mail, telefoon of opmerking in het event.
 *
 * Forms tekent in een open shadow root (<shopify-forms-embed>), die later verschijnt
 * dan deze sectie; vandaar de observer. Daarin komt ook de opmaak van de rondjes en
 * vinkjes (FORMS_CSS). Wordt die root ooit gesloten of anders opgebouwd, dan vallen
 * alleen het event en die opmaak weg; het formulier zelf werkt gewoon.
 */
/* De keuzevelden van Forms staan onder elkaar met rondjes en vinkjes van 12px: lang op
   een telefoon en te klein om te tikken. In hun eigen shadow root worden het tegels van
   gelijke breedte (desktop drie, telefoon twee naast elkaar), 44px hoog: zo blijft het
   formulier kort. Bij een keuze uit één (soort salon) is de tegel zelf de knop, gekozen
   in terracotta zoals de pillen in het ontwerp; bij meer keuzes staat het vinkje erin.
   Eén losse keuze (proefpakket) neemt de hele breedte. Focus blijft zichtbaar.

   Gemeten in DM Sans 14px: "Schoonheidssalon" 119px, "behandeling" 80px. Met 8px
   binnenrand past de langste soort op 390px en desktop op één regel; onder 380px
   wordt de tekst 13px. Afbreken met een streepje mag alleen met vijf letters aan
   weerszijden ("Schoonheids-salon", nooit "na-gels"). Gekozen is niet vet: dat liet
   de tegel verspringen. Zwevende labels blijven op één regel; "Telefoon (optioneel)"
   brak op 360px en liep door "+31" heen. */
const FORMS_CSS = `
  fieldset:has(input[type='radio']) > div,
  fieldset:has(input[type='checkbox']) > div {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
  }

  fieldset:has(input[type='checkbox']) > div:has(> label:only-child) {
    grid-template-columns: minmax(0, 1fr);
  }

  label:has(input[type='radio']),
  label:has(input[type='checkbox']) {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: auto;
    min-height: 44px;
    margin: 0;
    padding: 6px 10px;
    border: 1px solid #bdbdbd;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    box-sizing: border-box;
  }

  label:has(input[type='radio']) {
    padding-inline: 8px;
  }

  label:has(input[type='radio']) > span,
  label:has(input[type='checkbox']) > span {
    min-width: 0;
    font-size: 14px;
    line-height: 1.25;
    overflow-wrap: break-word;
    word-break: normal;
    hyphens: auto;
    -webkit-hyphenate-limit-before: 5;
    -webkit-hyphenate-limit-after: 5;
    hyphenate-limit-chars: auto 5 5;
  }

  label:has(input[type='checkbox']) > div {
    display: flex;
  }

  label > input[type='radio'] {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: 0;
    opacity: 0;
    pointer-events: none;
  }

  label input[type='checkbox'] {
    flex: none;
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: #c4572e;
  }

  label:has(input:checked) {
    border-color: #c4572e;
    background: #fcf7f4;
    box-shadow: inset 0 0 0 1px #c4572e;
  }

  label:has(input:focus-visible) {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  input + label,
  textarea + label {
    max-width: calc(100% - 32px);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  @media (max-width: 379px) {
    label:has(input[type='radio']) > span,
    label:has(input[type='checkbox']) > span {
      font-size: 13px;
    }
  }
`;

class SalonRequest extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #finder;
  /** @type {MutationObserver | undefined} */
  #watcher;
  /** @type {{ salon_type: string, uses: string[], trial: boolean } | null} */
  #pending = null;
  #attached = false;

  connectedCallback() {
    if (this.#attach()) return;
    this.#finder = new MutationObserver(() => {
      if (this.#attach()) this.#finder?.disconnect();
    });
    this.#finder.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#finder?.disconnect();
    this.#watcher?.disconnect();
  }

  #attach() {
    if (this.#attached) return true;
    const root = [...this.querySelectorAll('*')].find((element) => element.shadowRoot?.querySelector('form'))
      ?.shadowRoot;
    if (!root) return false;
    this.#attached = true;

    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(FORMS_CSS);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    } catch (error) {
      // zonder de extra opmaak werkt het formulier gewoon
    }

    root.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (form instanceof HTMLFormElement) this.#pending = this.#snapshot(form);
      },
      { capture: true }
    );

    this.#watcher = new MutationObserver(() => {
      if (!this.#pending || root.querySelector('form')) return;
      const payload = this.#pending;
      this.#pending = null;
      try {
        window.Shopify?.analytics?.publish('b2b_lead_submit', payload);
      } catch (error) {
        // tracking mag nooit in de weg zitten
      }
    });
    this.#watcher.observe(root, { childList: true, subtree: true });
    return true;
  }

  /** @param {HTMLFormElement} form */
  #snapshot(form) {
    /** @param {string | undefined} field */
    const checked = (field) =>
      field ? [...form.querySelectorAll(`input[name^="${CSS.escape(field)}"]:checked`)] : [];

    const type = checked(this.dataset.fieldType)[0];
    return {
      salon_type: type instanceof HTMLInputElement ? type.value : '',
      uses: checked(this.dataset.fieldUses).map((input) => (input instanceof HTMLInputElement ? input.value : '')),
      trial: checked(this.dataset.fieldTrial).length > 0,
    };
  }
}

if (!customElements.get('salon-request')) {
  customElements.define('salon-request', SalonRequest);
}
